import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { DateField } from '@shared/data/entity/fields/DateField';
import { Field, castField } from '@shared/data/entity/fields/Field';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { PersonField } from '@shared/data/entity/fields/PersonField';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { TagField } from '@shared/data/entity/fields/TagField';
import { TextField } from '@shared/data/entity/fields/TextField';
import { FieldRecord } from '@shared/scenes/entity/types';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import {
  isDateComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { omitTypeName } from '@shared/util/omitTypeName';

interface Props {
  workflowTemplate?: Pick<API.WorkflowTemplate, 'components'>;
  fields: FieldRecord;
  relatedCardComponent: RelatedCardComponent;
}

export function useFilterByComponents({
  workflowTemplate,
  fields,
  relatedCardComponent,
}: Props): API.FilterByComponent[] {
  return useMemo(() => {
    if (isNil(workflowTemplate)) {
      return [];
    }
    const filters =
      relatedCardComponent.listDefault?.filterByComponents?.filter(notNil).map(omitTypeName) ?? [];
    return filters
      .map(filter => getFilterByComponent(filter, workflowTemplate, fields))
      .filter(notNil);
  }, [workflowTemplate, fields, relatedCardComponent]);
}

function getFilterByComponent(
  filter: API.DynamicFilterByComponent,
  workflowTemplate: Pick<API.WorkflowTemplate, 'components'>,
  fields: FieldRecord,
): API.FilterByComponent | null {
  // The dynamic branch is destructured off rather than read in place, so that what's left is a
  // value `listWorkflowEntities` accepts. Its input is `FilterByComponent`, whose value union
  // doesn't define this field, and GraphQL rejects an input object carrying a field its type
  // doesn't define - including one set to null, which is what the fragment reads back for every
  // filter that doesn't use it.
  const { containsCurrentWorkflowTemplateComponentValue: match, ...value } = filter.value;
  const dynamicComponentId = match?.componentId;

  if (isNil(dynamicComponentId)) {
    // A filter with a value of its own - passed through as stored, `scalarKey` included: it names
    // the indexed representation the comparison runs against, so dropping it would filter the
    // wrong thing.
    return {
      componentId: filter.componentId,
      scalarKey: filter.scalarKey,
      value,
    };
  } else {
    const component = workflowTemplate.components.find(item => item?.id === filter.componentId);
    const field = fields[dynamicComponentId];
    if (isNil(component) || isNil(field)) {
      return null;
    }
    return getFilterByComponentFromField(component, field);
  }
}

function getFilterByComponentFromField(
  component: API.AnyComponent,
  field: Field,
): API.FilterByComponent | null {
  if (isTagComponent(component)) {
    const tagField = castField(field, TagField);
    return tagField ? getTagComponentFilter(component, tagField) : null;
  }
  if (isRelatedCardComponent(component)) {
    const relatedEntityField = castField(field, RelatedEntityField);
    return relatedEntityField
      ? getRelatedEntityComponentFilter(component, relatedEntityField)
      : null;
  }
  if (isPersonComponent(component)) {
    const personField = castField(field, PersonField);
    return personField ? getPersonComponentFilter(component, personField) : null;
  }
  if (isTextComponent(component)) {
    const textField = castField(field, TextField);
    return textField ? getTextFieldFilter(component, textField) : null;
  }
  if (isDateComponent(component)) {
    const dateField = castField(field, DateField);
    return dateField ? getDateComponentFilter(component, dateField) : null;
  }
  if (isNumberComponent(component)) {
    const numberField = castField(field, NumberField);
    return numberField ? getNumberComponentFilter(component, numberField) : null;
  }
  return null;
}

function getTagComponentFilter(
  component: API.TagComponent,
  field: TagField,
): API.FilterByComponent | null {
  const values = field.tagIds;
  if (isEmpty(values)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: { values, negate: false },
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getRelatedEntityComponentFilter(
  component: API.RelatedCardComponent,
  field: RelatedEntityField,
): API.FilterByComponent | null {
  const values = field.items.map(item => item.id);
  if (isEmpty(values)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: { values, negate: false },
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getPersonComponentFilter(
  component: API.PersonComponent,
  field: PersonField,
): API.FilterByComponent | null {
  const values = field.users.filter(notNil).map(user => user.id);
  if (isEmpty(values)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: {
        values,
        negate: false,
      },
      containsString: null,
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getTextFieldFilter(
  component: API.TextComponent,
  field: TextField,
): API.FilterByComponent | null {
  const value = field.value;
  if (isEmpty(value)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: null,
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: { value },
    },
  };
}

function getDateComponentFilter(
  component: API.DateComponent,
  field: DateField,
): API.FilterByComponent | null {
  if (isNil(field.date)) {
    return null;
  }
  const day = moment(field.date);
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: null,
      dateIs: null,
      dateRange: {
        before: day.endOf('day').toISOString(),
        after: day.startOf('day').toISOString(),
      },
      dateTokenIs: null,
      isEmpty: null,
      numberIs: null,
      stringWildcard: null,
    },
  };
}

function getNumberComponentFilter(
  component: API.NumberComponent,
  field: NumberField,
): API.FilterByComponent | null {
  const value = field.value;
  if (isEmpty(value)) {
    return null;
  }
  return {
    componentId: component.id,
    value: {
      containsCurrentUser: null,
      containsNumber: null,
      containsString: null,
      dateIs: null,
      dateRange: null,
      dateTokenIs: null,
      isEmpty: null,
      numberIs: { value, operator: 'EQ' },
      stringWildcard: null,
    },
  };
}
